/**
 * Edge Function client helper.
 *
 * All SDK mutations (scores, achievements, events, play tokens) go
 * through Edge Functions via this helper. Reads go through the Supabase
 * client directly (RLS enforces access).
 *
 * Deliberately uses `fetch` instead of `supabase.functions.invoke`
 * because we need `keepalive: true` on the beforeunload analytics flush
 * path — the invoke helper doesn't expose that option.
 */
import { VibecadeError, VibecadeErrorCode } from '../types';
import { errorFromResponse, wrapError } from './errors';
import type { SdkContext } from './context';

export interface InvokeOptions {
  /** When true, the request survives page unload. Used by track-events flush. */
  keepalive?: boolean;
}

/**
 * Upper bound on a single Edge Function round trip. A stalled connection
 * (dead wifi, a tab woken from sleep mid-request) would otherwise hang
 * the calling submitScore/unlockAchievement/flushEvents forever — fetch
 * has no default timeout.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Call an Edge Function by name, automatically attaching the current
 * player's JWT. Throws VibecadeError on any failure (HTTP non-2xx,
 * network, timeout, or parse error).
 *
 * The generic `T` is the expected response shape; no runtime validation
 * is performed on the body — callers trust the Edge Function's contract.
 * The request body is serialized before any network work starts, so a
 * non-JSON-serializable body fails as INVALID_BODY instead of surfacing
 * as a misleading network error.
 */
export async function invokeEdge<T>(
  ctx: SdkContext,
  fnName: string,
  body: Record<string, unknown>,
  opts: InvokeOptions = {},
): Promise<T> {
  const token = await ctx.accessToken();
  if (!token) {
    throw new VibecadeError(
      VibecadeErrorCode.NotInitialized,
      'Call init() before making SDK calls that require a session',
    );
  }

  // Serialized up front, outside the network try/catch below — a
  // circular-reference body (e.g. game-supplied `meta`) must fail as
  // INVALID_BODY, not get misreported as a NETWORK_ERROR once it reaches
  // the fetch call.
  let serializedBody: string;
  try {
    serializedBody = JSON.stringify(body);
  } catch (e) {
    throw new VibecadeError(
      VibecadeErrorCode.InvalidBody,
      `${fnName}: request body is not JSON-serializable`,
      e,
    );
  }

  const url = `${ctx.config.apiUrl}/functions/v1/${fnName}`;

  // AbortSignal.timeout() would be shorter, but Safari < 15.4 doesn't
  // implement it and the SDK still has to run there — so the controller
  // and timer are wired up by hand instead.
  const controller = new AbortController();
  // Set synchronously by the timer callback before it calls abort(), so
  // the catch block below can tell a timeout apart from every other
  // fetch rejection deterministically — DOMException's `name`/`message`
  // for an aborted fetch aren't consistent across runtimes, but this
  // flag is.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        apikey: ctx.config.anonKey,
      },
      body: serializedBody,
      keepalive: opts.keepalive,
      signal: controller.signal,
    });
  } catch (e) {
    if (timedOut) {
      throw new VibecadeError(
        VibecadeErrorCode.NetworkError,
        `${fnName} timed out after ${REQUEST_TIMEOUT_MS / 1000}s`,
      );
    }
    throw wrapError(e, VibecadeErrorCode.NetworkError, `Could not reach ${fnName}`);
  } finally {
    // Always cleared: on success this stops a late abort() from firing
    // after the response already arrived; on failure the timer has
    // usually already fired, and clearing it again is a harmless no-op.
    clearTimeout(timer);
  }

  if (!res.ok) {
    throw await errorFromResponse(res);
  }

  try {
    return (await res.json()) as T;
  } catch (e) {
    throw wrapError(e, VibecadeErrorCode.InternalError, `Malformed response from ${fnName}`);
  }
}
